import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './DocField.module.css';
import { cx } from '../../lib/cx';

export interface DocFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  mono?: boolean;
  wide?: boolean;
  rightSlot?: ReactNode;
}

/**
 * Textový input stylizovaný pro velké dokumentové formuláře.
 * Větší touch target (min-height 52 px), 17 px font — odpovídá F2Field z handoffu.
 */
export const DocField = forwardRef<HTMLInputElement, DocFieldProps>(function DocField(
  { label, hint, required, mono, wide, rightSlot, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={cx(styles.field, wide && styles.fieldWide, className)}>
      <div className={styles.labelRow}>
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
        {hint && <span className={styles.hint}>{hint}</span>}
      </div>
      <div className={styles.control}>
        <input ref={ref} id={inputId} className={cx(styles.input, mono && styles.mono)} {...rest} />
        {rightSlot}
      </div>
    </div>
  );
});

export interface DocGroupProps {
  idx: ReactNode;
  title: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}

export function DocGroup({ idx, title, note, children }: DocGroupProps) {
  return (
    <section className={styles.group}>
      <div className={styles.groupHead}>
        <div className={styles.groupBadge}>{idx}</div>
        <div className={styles.groupTitleText}>
          <div className={styles.groupTitle}>{title}</div>
          {note && <div className={styles.groupNote}>{note}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function DocGrid2({ children }: { children: ReactNode }) {
  return <div className={styles.grid2}>{children}</div>;
}

export function DocGrid3({ children }: { children: ReactNode }) {
  return <div className={styles.grid3}>{children}</div>;
}

export interface DocChipRowItem {
  id: string;
  label: ReactNode;
  checked: boolean;
}

export interface DocChipRowProps {
  label?: ReactNode;
  items: DocChipRowItem[];
  onToggle: (id: string) => void;
  /** Pokud dodáno, zobrazí se vstup pro přidání vlastní položky + Enter/klik přidá. */
  onAdd?: (label: string) => void;
  /** Pokud dodáno, u každého chipu se zobrazí ✕ (pouze pro uživatelem přidané). */
  onRemove?: (id: string) => void;
  /** Které chipy nelze odebrat (standardní sada). */
  fixedIds?: string[];
}

export function DocChipRow({ label, items, onToggle, onAdd, onRemove, fixedIds }: DocChipRowProps) {
  const [draft, setDraft] = useState('');
  function submit() {
    const v = draft.trim();
    if (!v || !onAdd) return;
    onAdd(v);
    setDraft('');
  }
  return (
    <div>
      {label && <div className={styles.chipLabel}>{label}</div>}
      <div className={styles.chipRow}>
        {items.map((item) => {
          const fixed = fixedIds?.includes(item.id);
          return (
            <span key={item.id} className={styles.chipWrap}>
              <button
                type="button"
                className={cx(styles.chip, item.checked && styles.on)}
                onClick={() => onToggle(item.id)}
                aria-pressed={item.checked}
              >
                <span className={styles.chipBox} aria-hidden>
                  {item.checked ? '✓' : ''}
                </span>
                {item.label}
              </button>
              {onRemove && !fixed && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => onRemove(item.id)}
                  aria-label="Odebrat"
                  title="Odebrat položku"
                >
                  ✕
                </button>
              )}
            </span>
          );
        })}
        {onAdd && (
          <span className={styles.chipAdd}>
            <input
              type="text"
              className={styles.chipInput}
              placeholder="+ přidat vlastní"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <button
              type="button"
              className={styles.chipAddBtn}
              onClick={submit}
              disabled={!draft.trim()}
            >
              Přidat
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

export function DocVerifiedBadge({ children = 'ARES ✓' }: { children?: ReactNode }) {
  return <span className={styles.verified}>{children}</span>;
}

export function DocRightLink({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <span className={styles.rightSlot} onClick={onClick} role={onClick ? 'button' : undefined}>
      {children}
    </span>
  );
}
